import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const API_URL = 'https://iia-backend-production.up.railway.app';

const FacultyHall = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('students');

  // Redirect students
  useEffect(() => {
    if (user && user.role_id === 4) {
      navigate('/campus');
    }
  }, [user, navigate]);

  const sections = [
    { id: 'students', label: '👥 My Students' },
    { id: 'assignments', label: '🔗 Advisor Assignments' },
    { id: 'flags', label: '🚩 Student Flags' },
    { id: 'track-overview', label: '📊 Track Overview' },
    { id: 'inbox', label: '✉️ Student Inbox' },
    { id: 'announcements', label: '📢 Staff Announcements' },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>IIA</h1>
          <span style={styles.logoText}>Impact Influence Academy</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.welcome}>Welcome, {user?.first_name}</span>
          <button style={styles.returnBtn} onClick={() => navigate('/campus')}>
            ← Return to Campus Square
          </button>
        </div>
      </div>

      <div style={styles.main}>
        <div style={styles.pageHeader}>
          <h2 style={styles.pageTitle}>🏛️ Faculty Hall</h2>
          <p style={styles.pageSubtitle}>Staff workspace — students, progress, messaging, and announcements</p>
        </div>

        <div style={styles.tabs}>
          {sections.map(section => (
            <button
              key={section.id}
              style={activeSection === section.id ? styles.tabActive : styles.tab}
              onClick={() => setActiveSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </div>

        <div style={styles.content}>
          {activeSection === 'students' && <MyStudents user={user} />}
          {activeSection === 'assignments' && <AdvisorAssignments user={user} />}
          {activeSection === 'flags' && <StudentFlags user={user} />}
          {activeSection === 'track-overview' && <TrackOverview />}
          {activeSection === 'inbox' && <StudentInbox user={user} />}
          {activeSection === 'announcements' && <StaffAnnouncements user={user} />}
        </div>
      </div>
    </div>
  );
};

// ==================== MY STUDENTS ====================
const MyStudents = ({ user }) => {
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [progress, setProgress] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [flagForm, setFlagForm] = useState({ flag_type: 'needs_attention', note: '' });
  const [showFlagForm, setShowFlagForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => { fetchStudents(); }, []);

  useEffect(() => {
    if (selected) {
      fetchProgress(selected.id);
      fetchNotes(selected.id);
    }
  }, [selected]);

  const fetchStudents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/faculty/students`);
      setStudents(res.data.students);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async (studentId) => {
    try {
      const res = await axios.get(`${API_URL}/api/faculty/students/${studentId}/progress`);
      setProgress(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchNotes = async (studentId) => {
    try {
      const res = await axios.get(`${API_URL}/api/faculty/notes/${studentId}`);
      setNotes(res.data.notes);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API_URL}/api/faculty/notes/${selected.id}`, {
        note_text: newNote
      });
      setNewNote('');
      fetchNotes(selected.id);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddFlag = async () => {
    try {
      await axios.post(`${API_URL}/api/faculty/flags`, {
        student_id: selected.id,
        flag_type: flagForm.flag_type,
        note: flagForm.note,
      });
      setMessage('Flag added');
      setShowFlagForm(false);
      setFlagForm({ flag_type: 'needs_attention', note: '' });
      fetchStudents();
      fetchProgress(selected.id);
    } catch (err) {
      console.error(err);
    }
  };

  const flagColor = (type) => {
    if (type === 'at_risk') return '#E74C3C';
    if (type === 'excelling') return '#1E8449';
    return '#C49A28';
  };

  const flagLabel = (type) => {
    if (type === 'at_risk') return '⚠️ At Risk';
    if (type === 'excelling') return '⭐ Excelling';
    return '👀 Needs Attention';
  };

  return (
    <div style={styles.splitView}>
      {/* Student List */}
      <div style={styles.listPanel}>
        {loading && <p style={styles.loadingText}>Loading...</p>}
        {!loading && students.length === 0 && (
          <p style={styles.emptyText}>No students assigned yet.</p>
        )}
        {students.map(student => (
          <div
            key={student.id}
            style={selected?.id === student.id ? styles.listItemActive : styles.listItem}
            onClick={() => setSelected(student)}
          >
            <div style={styles.listItemLeft}>
              <div style={styles.avatar}>
                {student.first_name?.[0]}{student.last_name?.[0]}
              </div>
              <div>
                <p style={styles.listItemName}>
                  {student.first_name} {student.last_name}
                  {student.active_flag && (
                    <span style={{
                      ...styles.flagPill,
                      backgroundColor: `${flagColor(student.active_flag)}20`,
                      color: flagColor(student.active_flag),
                    }}>
                      {flagLabel(student.active_flag)}
                    </span>
                  )}
                </p>
                <p style={styles.listItemSub}>{student.email}</p>
                {student.enrolled_paths?.length > 0 && (
                  <p style={styles.listItemPaths}>
                    {student.enrolled_paths.join(', ')}
                  </p>
                )}
              </div>
            </div>
            <div style={styles.listItemRight}>
              <span style={{
                ...styles.statusDot,
                backgroundColor: student.status === 'active' ? '#1E8449' : '#ff6666'
              }} />
            </div>
          </div>
        ))}
      </div>

      {/* Student Detail */}
      <div style={styles.detailPanel}>
        {!selected && (
          <div style={styles.noSelection}>
            <p style={styles.noSelectionText}>Select a student to view their profile</p>
          </div>
        )}

        {selected && progress && (
          <>
            {message && <p style={styles.successText}>{message}</p>}

            {/* Header */}
            <div style={styles.studentDetailHeader}>
              <div style={styles.studentDetailAvatar}>
                {selected.first_name?.[0]}{selected.last_name?.[0]}
              </div>
              <div>
                <h3 style={styles.studentDetailName}>
                  {selected.first_name} {selected.last_name}
                </h3>
                <p style={styles.studentDetailEmail}>{selected.email}</p>
              </div>
              <button
                style={styles.flagBtn}
                onClick={() => setShowFlagForm(!showFlagForm)}
              >
                🚩 Add Flag
              </button>
            </div>

            {showFlagForm && (
              <div style={styles.flagForm}>
                <select
                  style={styles.select}
                  value={flagForm.flag_type}
                  onChange={e => setFlagForm({...flagForm, flag_type: e.target.value})}
                >
                  <option value="needs_attention">👀 Needs Attention</option>
                  <option value="at_risk">⚠️ At Risk</option>
                  <option value="excelling">⭐ Excelling</option>
                </select>
                <input
                  style={styles.input}
                  placeholder="Note (optional)"
                  value={flagForm.note}
                  onChange={e => setFlagForm({...flagForm, note: e.target.value})}
                />
                <div style={styles.formActions}>
                  <button style={styles.saveBtn} onClick={handleAddFlag}>Add Flag</button>
                  <button style={styles.cancelBtn} onClick={() => setShowFlagForm(false)}>Cancel</button>
                </div>
              </div>
            )}

            {/* Stats */}
            <div style={styles.statsRow}>
              <div style={styles.statCard}>
                <span style={styles.statNum}>{progress.enrollments?.length || 0}</span>
                <span style={styles.statLbl}>Paths</span>
              </div>
              <div style={styles.statCard}>
                <span style={styles.statNum}>{progress.progress?.length || 0}</span>
                <span style={styles.statLbl}>Tasks Done</span>
              </div>
              <div style={styles.statCard}>
                <span style={styles.statNum}>
                  {progress.flags?.filter(f => !f.cleared_at).length || 0}
                </span>
                <span style={styles.statLbl}>Active Flags</span>
              </div>
              <div style={styles.statCard}>
                <span style={styles.statNum}>
                  {progress.progress?.length > 0
                    ? new Date(progress.progress[0].completed_at).toLocaleDateString()
                    : '--'}
                </span>
                <span style={styles.statLbl}>Last Active</span>
              </div>
            </div>

            {/* Enrolled Paths */}
            {progress.enrollments?.length > 0 && (
              <div style={styles.detailSection}>
                <h4 style={styles.detailSectionTitle}>Enrolled Paths</h4>
                {progress.enrollments.map(e => (
                  <div key={e.id} style={styles.pathRow}>
                    <span style={styles.pathName}>🎓 {e.path_title}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Active Flags */}
            {progress.flags?.filter(f => !f.cleared_at).length > 0 && (
              <div style={styles.detailSection}>
                <h4 style={styles.detailSectionTitle}>Active Flags</h4>
                {progress.flags.filter(f => !f.cleared_at).map(flag => (
                  <div key={flag.id} style={styles.flagRow}>
                    <span style={{
                      ...styles.flagPill,
                      backgroundColor: `${flagColor(flag.flag_type)}20`,
                      color: flagColor(flag.flag_type),
                    }}>
                      {flagLabel(flag.flag_type)}
                    </span>
                    {flag.note && <p style={styles.flagNote}>{flag.note}</p>}
                    <p style={styles.flagMeta}>
                      By {flag.first_name} {flag.last_name} · {new Date(flag.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Advisor Notes */}
            <div style={styles.detailSection}>
              <h4 style={styles.detailSectionTitle}>Advisor Notes</h4>
              <div style={styles.notesBox}>
                {notes.length === 0 && (
                  <p style={styles.emptyText}>No notes yet.</p>
                )}
                {notes.map(note => (
                  <div key={note.id} style={styles.noteRow}>
                    <p style={styles.noteText}>{note.note_text}</p>
                    <p style={styles.noteMeta}>
                      {note.first_name} {note.last_name} · {new Date(note.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
              <div style={styles.newNoteBox}>
                <textarea
                  style={styles.textarea}
                  placeholder="Add a private note..."
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  rows={3}
                />
                <button style={styles.saveBtn} onClick={handleAddNote}>
                  Add Note
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
// ==================== ADVISOR ASSIGNMENTS ====================
const AdvisorAssignments = ({ user }) => {
  const [assignments, setAssignments] = useState([]);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ advisor_id: '', student_id: '' });
  const [message, setMessage] = useState('');
  const isAdmin = user?.role_id === 1;

  useEffect(() => {
    fetchAssignments();
    fetchStudentsAndStaff();
  }, []);

  const fetchAssignments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/faculty/assignments`);
      setAssignments(res.data.assignments);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchStudentsAndStaff = async () => {
    try {
      const [studentsRes, staffRes] = await Promise.all([
        axios.get(`${API_URL}/api/students`),
        axios.get(`${API_URL}/api/faculty/staff-members`),
      ]);
      setStudents(studentsRes.data.students);
      setStaff(staffRes.data.staff);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreate = async () => {
    if (!form.advisor_id || !form.student_id) return;
    try {
      await axios.post(`${API_URL}/api/faculty/assignments`, form);
      setMessage('Assignment created');
      setForm({ advisor_id: '', student_id: '' });
      fetchAssignments();
    } catch (err) {
      setMessage('Error creating assignment');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this assignment?')) return;
    try {
      await axios.delete(`${API_URL}/api/faculty/assignments/${id}`);
      fetchAssignments();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      {isAdmin && (
        <div style={styles.formCard}>
          <h3 style={styles.formCardTitle}>Assign Student to Advisor</h3>
          {message && <p style={styles.successText}>{message}</p>}
          <div style={styles.formRow}>
            <select
              style={styles.select}
              value={form.advisor_id}
              onChange={e => setForm({...form, advisor_id: e.target.value})}
            >
              <option value="">Select advisor...</option>
              {staff.map(s => (
                <option key={s.id} value={s.id}>
                  {s.first_name} {s.last_name}
                </option>
              ))}
            </select>
            <select
              style={styles.select}
              value={form.student_id}
              onChange={e => setForm({...form, student_id: e.target.value})}
            >
              <option value="">Select student...</option>
              {students.map(s => (
                <option key={s.id} value={s.id}>
                  {s.first_name} {s.last_name}
                </option>
              ))}
            </select>
          </div>
          <button style={styles.saveBtn} onClick={handleCreate}>
            Create Assignment
          </button>
        </div>
      )}

      <div style={styles.assignmentsList}>
        {assignments.length === 0 && (
          <p style={styles.emptyText}>No assignments yet.</p>
        )}
        {assignments.map(a => (
          <div key={a.id} style={styles.assignmentRow}>
            <div style={styles.assignmentLeft}>
              <div>
                <p style={styles.assignmentAdvisor}>
                  {a.advisor_first} {a.advisor_last}
                  <span style={styles.advisorLabel}> Advisor</span>
                </p>
                <p style={styles.assignmentStudent}>
                  → {a.student_first} {a.student_last}
                  <span style={styles.studentEmail}> {a.student_email}</span>
                </p>
              </div>
            </div>
            {isAdmin && (
              <button
                style={styles.removeBtn}
                onClick={() => handleDelete(a.id)}
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== STUDENT FLAGS ====================
const StudentFlags = ({ user }) => {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchFlags(); }, []);

  const fetchFlags = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/faculty/flags`);
      setFlags(res.data.flags);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async (flagId) => {
    try {
      await axios.patch(`${API_URL}/api/faculty/flags/${flagId}/clear`);
      fetchFlags();
    } catch (err) {
      console.error(err);
    }
  };

  const flagColor = (type) => {
    if (type === 'at_risk') return '#E74C3C';
    if (type === 'excelling') return '#1E8449';
    return '#C49A28';
  };

  const flagLabel = (type) => {
    if (type === 'at_risk') return '⚠️ At Risk';
    if (type === 'excelling') return '⭐ Excelling';
    return '👀 Needs Attention';
  };

  return (
    <div>
      <div style={styles.flagsHeader}>
        <h3 style={styles.sectionTitle}>Active Student Flags</h3>
        <p style={styles.sectionSubtitle}>
          {flags.length} active {flags.length === 1 ? 'flag' : 'flags'}
        </p>
      </div>

      {loading && <p style={styles.loadingText}>Loading...</p>}
      {!loading && flags.length === 0 && (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>No active flags. All students are on track.</p>
        </div>
      )}

      {flags.map(flag => (
        <div key={flag.id} style={styles.flagCard}>
          <div style={styles.flagCardLeft}>
            <span style={{
              ...styles.flagPill,
              backgroundColor: `${flagColor(flag.flag_type)}20`,
              color: flagColor(flag.flag_type),
              fontSize: '13px',
              padding: '4px 12px',
            }}>
              {flagLabel(flag.flag_type)}
            </span>
            <div style={styles.flagCardInfo}>
              <p style={styles.flagStudentName}>
                {flag.student_first} {flag.student_last}
              </p>
              {flag.note && <p style={styles.flagCardNote}>{flag.note}</p>}
              <p style={styles.flagCardMeta}>
                Flagged by {flag.flagged_by_first} {flag.flagged_by_last} · {new Date(flag.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          <button style={styles.clearFlagBtn} onClick={() => handleClear(flag.id)}>
            Clear Flag
          </button>
        </div>
      ))}
    </div>
  );
};

// ==================== TRACK OVERVIEW ====================
const TrackOverview = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchTracks(); }, []);

  const fetchTracks = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/faculty/track-overview`);
      setTracks(res.data.tracks);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const totalStudents = tracks.reduce((sum, t) => sum + parseInt(t.student_count || 0), 0);

  return (
    <div>
      <div style={styles.trackHeader}>
        <h3 style={styles.sectionTitle}>Track Overview</h3>
        <p style={styles.sectionSubtitle}>{totalStudents} total enrolled students</p>
      </div>

      {loading && <p style={styles.loadingText}>Loading...</p>}

      <div style={styles.tracksGrid}>
        {tracks.map(track => (
          <div key={track.id} style={styles.trackCard}>
            <h4 style={styles.trackTitle}>{track.title}</h4>
            <div style={styles.trackStats}>
              <div style={styles.trackStat}>
                <span style={styles.trackStatNum}>{track.student_count}</span>
                <span style={styles.trackStatLbl}>Students</span>
              </div>
              <div style={styles.trackStat}>
                <span style={styles.trackStatNum}>{track.avg_completion || 0}%</span>
                <span style={styles.trackStatLbl}>Avg Completion</span>
              </div>
            </div>
            <div style={styles.progressBarBg}>
              <div style={{
                ...styles.progressBarFill,
                width: `${track.avg_completion || 0}%`
              }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
// ==================== STUDENT INBOX ====================
const StudentInbox = ({ user }) => {
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchConversations(); }, []);
  useEffect(() => { if (activeConv) fetchMessages(activeConv.id); }, [activeConv]);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/faculty/conversations`);
      setConversations(res.data.conversations);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (convId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/academic/conversations/${convId}/messages`
      );
      setMessages(res.data.messages);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    try {
      await axios.post(
        `${API_URL}/api/academic/conversations/${activeConv.id}/messages`,
        { body: newMessage }
      );
      setNewMessage('');
      fetchMessages(activeConv.id);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div style={styles.messagesContainer}>
      <div style={styles.convList}>
        <h3 style={styles.convListTitle}>Student Messages</h3>
        {loading && <p style={styles.loadingText}>Loading...</p>}
        {!loading && conversations.length === 0 && (
          <p style={styles.emptyTextSmall}>No student messages yet.</p>
        )}
        {conversations.map(conv => (
          <div
            key={conv.id}
            style={activeConv?.id === conv.id ? styles.convItemActive : styles.convItem}
            onClick={() => setActiveConv(conv)}
          >
            <p style={styles.convName}>
              {conv.student_first} {conv.student_last}
            </p>
            {conv.last_message && (
              <p style={styles.convPreview}>{conv.last_message}</p>
            )}
            {conv.unread_count > 0 && (
              <span style={styles.unreadBadge}>{conv.unread_count}</span>
            )}
          </div>
        ))}
      </div>

      <div style={styles.messageThread}>
        {!activeConv && (
          <div style={styles.noConvSelected}>
            <p style={styles.noConvText}>Select a conversation to reply</p>
          </div>
        )}
        {activeConv && (
          <>
            <div style={styles.threadHeader}>
              <h4 style={styles.threadName}>
                {activeConv.student_first} {activeConv.student_last}
              </h4>
            </div>
            <div style={styles.messagesList}>
              {messages.map(msg => (
                <div
                  key={msg.id}
                  style={msg.sender_id === user?.id ?
                    styles.messageSent : styles.messageReceived}
                >
                  <p style={styles.messageBody}>{msg.body}</p>
                  <p style={styles.messageTime}>
                    {new Date(msg.created_at).toLocaleTimeString('en-US', {
                      hour: 'numeric', minute: '2-digit'
                    })}
                  </p>
                </div>
              ))}
            </div>
            <div style={styles.messageInput}>
              <input
                style={styles.messageField}
                placeholder="Type a reply..."
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
              />
              <button style={styles.sendBtn} onClick={handleSend}>Send</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== STAFF ANNOUNCEMENTS ====================
const StaffAnnouncements = ({ user }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', body: '' });
  const [message, setMessage] = useState('');
  const isAdmin = user?.role_id === 1;

  useEffect(() => { fetchAnnouncements(); }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/faculty/announcements`);
      setAnnouncements(res.data.announcements);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreate = async () => {
    if (!form.title || !form.body) return;
    try {
      await axios.post(`${API_URL}/api/faculty/announcements`, form);
      setMessage('Announcement posted');
      setShowForm(false);
      setForm({ title: '', body: '' });
      fetchAnnouncements();
    } catch (err) {
      setMessage('Error posting announcement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API_URL}/api/faculty/announcements/${id}`);
      fetchAnnouncements();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div style={styles.annBoardHeader}>
        <h3 style={styles.sectionTitle}>Staff Announcements</h3>
        <button style={styles.addBtn} onClick={() => setShowForm(!showForm)}>
          + Post
        </button>
      </div>

      {message && <p style={styles.successText}>{message}</p>}

      {showForm && (
        <div style={styles.formCard}>
          <input
            style={styles.input}
            placeholder="Announcement title"
            value={form.title}
            onChange={e => setForm({...form, title: e.target.value})}
          />
          <textarea
            style={styles.textarea}
            placeholder="Announcement body"
            value={form.body}
            onChange={e => setForm({...form, body: e.target.value})}
            rows={4}
          />
          <div style={styles.formActions}>
            <button style={styles.saveBtn} onClick={handleCreate}>Post</button>
            <button style={styles.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {announcements.length === 0 && !showForm && (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>No staff announcements yet.</p>
        </div>
      )}

      {announcements.map(ann => (
        <div key={ann.id} style={styles.annCard}>
          <div style={styles.annCardHeader}>
            <h4 style={styles.annCardTitle}>{ann.title}</h4>
            {isAdmin && (
              <button
                style={styles.deleteSmallBtn}
                onClick={() => handleDelete(ann.id)}
              >
                Delete
              </button>
            )}
          </div>
          <p style={styles.annCardBody}>{ann.body}</p>
          <p style={styles.annCardMeta}>
            {ann.first_name} {ann.last_name} · {new Date(ann.created_at).toLocaleDateString()}
          </p>
        </div>
      ))}
    </div>
  );
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#1A2744', color: '#ffffff' },
  header: { backgroundColor: '#0f1a33', borderBottom: '1px solid #C49A28', padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  logo: { color: '#C49A28', fontSize: '24px', fontWeight: '800', margin: '0', letterSpacing: '2px' },
  logoText: { color: '#ffffff80', fontSize: '14px', letterSpacing: '1px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  welcome: { color: '#ffffff', fontSize: '14px' },
  returnBtn: { backgroundColor: 'transparent', border: '1px solid #C49A28', color: '#C49A28', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  main: { maxWidth: '1200px', margin: '0 auto', padding: '32px' },
  pageHeader: { marginBottom: '24px' },
  pageTitle: { color: '#C49A28', fontSize: '28px', fontWeight: '700', margin: '0 0 4px 0' },
  pageSubtitle: { color: '#ffffff60', fontSize: '14px', margin: '0' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #ffffff15', flexWrap: 'wrap' },
  tab: { backgroundColor: 'transparent', border: 'none', color: '#ffffff60', padding: '12px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', borderBottom: '2px solid transparent' },
  tabActive: { backgroundColor: 'transparent', border: 'none', color: '#C49A28', padding: '12px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', borderBottom: '2px solid #C49A28' },
  content: { minHeight: '400px' },
  splitView: { display: 'grid', gridTemplateColumns: '300px 1fr', gap: '0', minHeight: '600px' },
  listPanel: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '10px 0 0 10px', overflowY: 'auto', maxHeight: '700px' },
  listItem: { padding: '14px 16px', borderBottom: '1px solid #ffffff08', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  listItemActive: { padding: '14px 16px', borderBottom: '1px solid #ffffff08', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#1A2744' },
  listItemLeft: { display: 'flex', alignItems: 'flex-start', gap: '10px' },
  listItemRight: { flexShrink: 0 },
  avatar: { width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#1A6B8A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: '13px', fontWeight: '700', flexShrink: 0 },
  listItemName: { color: '#ffffff', fontSize: '13px', fontWeight: '600', margin: '0 0 2px 0' },
  listItemSub: { color: '#ffffff50', fontSize: '11px', margin: '0' },
  listItemPaths: { color: '#C49A28', fontSize: '11px', margin: '2px 0 0 0' },
  statusDot: { width: '8px', height: '8px', borderRadius: '50%', display: 'block' },
  flagPill: { fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px', marginLeft: '6px', display: 'inline-block' },
  detailPanel: { backgroundColor: '#0d1628', border: '1px solid #ffffff15', borderLeft: 'none', borderRadius: '0 10px 10px 0', padding: '24px', overflowY: 'auto', maxHeight: '700px' },
  noSelection: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' },
  noSelectionText: { color: '#ffffff30', fontSize: '14px' },
  studentDetailHeader: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #ffffff10' },
  studentDetailAvatar: { width: '52px', height: '52px', borderRadius: '50%', backgroundColor: '#1A6B8A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: '18px', fontWeight: '700', flexShrink: 0 },
  studentDetailName: { color: '#ffffff', fontSize: '18px', fontWeight: '700', margin: '0 0 4px 0' },
  studentDetailEmail: { color: '#C49A28', fontSize: '13px', margin: '0' },
  flagBtn: { marginLeft: 'auto', backgroundColor: 'transparent', border: '1px solid #C49A2840', color: '#C49A28', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', cursor: 'pointer' },
  flagForm: { backgroundColor: '#1A2744', borderRadius: '8px', padding: '16px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' },
  statCard: { backgroundColor: '#1A2744', borderRadius: '8px', padding: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', textAlign: 'center' },
  statNum: { color: '#C49A28', fontSize: '18px', fontWeight: '700' },
  statLbl: { color: '#ffffff50', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px' },
  detailSection: { marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #ffffff08' },
  detailSectionTitle: { color: '#ffffff50', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 10px 0' },
  pathRow: { backgroundColor: '#1A2744', borderRadius: '6px', padding: '8px 12px', marginBottom: '6px' },
  pathName: { color: '#ffffff', fontSize: '13px' },
  flagRow: { backgroundColor: '#1A2744', borderRadius: '8px', padding: '12px', marginBottom: '8px' },
  flagNote: { color: '#ffffffcc', fontSize: '13px', margin: '6px 0 4px 0' },
  flagMeta: { color: '#ffffff40', fontSize: '11px', margin: '0' },
  notesBox: { backgroundColor: '#1A2744', borderRadius: '8px', padding: '12px', marginBottom: '10px', maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' },
  noteRow: { borderBottom: '1px solid #ffffff08', paddingBottom: '8px' },
  noteText: { color: '#ffffffcc', fontSize: '13px', margin: '0 0 4px 0', lineHeight: '1.5' },
  noteMeta: { color: '#ffffff40', fontSize: '11px', margin: '0' },
  newNoteBox: { display: 'flex', flexDirection: 'column', gap: '8px' },
  formCard: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '10px', padding: '20px', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '10px' },
  formCardTitle: { color: '#ffffff', fontSize: '15px', fontWeight: '600', margin: '0' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  formActions: { display: 'flex', gap: '8px' },
  select: { backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px 14px', color: '#ffffff', fontSize: '13px', outline: 'none', width: '100%' },
  input: { backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px 14px', color: '#ffffff', fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  textarea: { backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px 14px', color: '#ffffff', fontSize: '13px', outline: 'none', resize: 'vertical', width: '100%', boxSizing: 'border-box' },
  saveBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '8px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ffffff30', color: '#ffffff60', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer' },
  addBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  removeBtn: { backgroundColor: 'transparent', border: '1px solid #ff000030', color: '#ff6666', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer' },
  deleteSmallBtn: { backgroundColor: 'transparent', border: '1px solid #ff000030', color: '#ff6666', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer' },
  assignmentsList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  assignmentRow: { backgroundColor: '#0f1a33', border: '1px solid #ffffff10', borderRadius: '8px', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  assignmentLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  assignmentAdvisor: { color: '#ffffff', fontSize: '14px', fontWeight: '600', margin: '0 0 4px 0' },
  advisorLabel: { color: '#C49A28', fontSize: '11px', fontWeight: '400' },
  assignmentStudent: { color: '#ffffff80', fontSize: '13px', margin: '0' },
  studentEmail: { color: '#ffffff40', fontSize: '11px' },
  flagsHeader: { marginBottom: '20px' },
  flagCard: { backgroundColor: '#0f1a33', border: '1px solid #ffffff10', borderRadius: '10px', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' },
  flagCardLeft: { display: 'flex', alignItems: 'flex-start', gap: '14px' },
  flagCardInfo: { display: 'flex', flexDirection: 'column', gap: '4px' },
  flagStudentName: { color: '#ffffff', fontSize: '14px', fontWeight: '600', margin: '0' },
  flagCardNote: { color: '#ffffff80', fontSize: '13px', margin: '0' },
  flagCardMeta: { color: '#ffffff40', fontSize: '11px', margin: '0' },
  clearFlagBtn: { backgroundColor: 'transparent', border: '1px solid #ffffff20', color: '#ffffff60', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', cursor: 'pointer' },
  trackHeader: { marginBottom: '20px' },
  tracksGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' },
  trackCard: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '12px', padding: '24px' },
  trackTitle: { color: '#C49A28', fontSize: '16px', fontWeight: '700', margin: '0 0 16px 0' },
  trackStats: { display: 'flex', gap: '24px', marginBottom: '16px' },
  trackStat: { display: 'flex', flexDirection: 'column', gap: '4px' },
  trackStatNum: { color: '#ffffff', fontSize: '24px', fontWeight: '700' },
  trackStatLbl: { color: '#ffffff50', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' },
  progressBarBg: { backgroundColor: '#ffffff10', borderRadius: '4px', height: '6px', overflow: 'hidden' },
  progressBarFill: { backgroundColor: '#C49A28', height: '100%', borderRadius: '4px', transition: 'width 0.3s ease' },
  sectionTitle: { color: '#ffffff', fontSize: '18px', fontWeight: '700', margin: '0 0 4px 0' },
  sectionSubtitle: { color: '#ffffff60', fontSize: '13px', margin: '0' },
  emptyState: { backgroundColor: '#0f1a33', border: '1px solid #ffffff10', borderRadius: '10px', padding: '40px', textAlign: 'center' },
  emptyText: { color: '#ffffff30', fontSize: '14px', margin: '0' },
  emptyTextSmall: { color: '#ffffff30', fontSize: '13px', padding: '16px' },
  loadingText: { color: '#ffffff60', fontSize: '14px' },
  successText: { color: '#1E8449', fontSize: '13px', margin: '0 0 12px 0' },
  annBoardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  annCard: { backgroundColor: '#0f1a33', border: '1px solid #ffffff10', borderRadius: '10px', padding: '20px', marginBottom: '12px' },
  annCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  annCardTitle: { color: '#C49A28', fontSize: '15px', fontWeight: '700', margin: '0' },
  annCardBody: { color: '#ffffffcc', fontSize: '14px', lineHeight: '1.7', margin: '0 0 8px 0' },
  annCardMeta: { color: '#ffffff40', fontSize: '12px', margin: '0' },
  messagesContainer: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: '0', minHeight: '500px' },
  convList: { backgroundColor: '#0f1a33', borderRadius: '10px 0 0 10px', border: '1px solid #ffffff15', overflow: 'hidden' },
  convListTitle: { color: '#ffffff', fontSize: '14px', fontWeight: '600', padding: '16px', margin: '0', borderBottom: '1px solid #ffffff10' },
  convItem: { padding: '14px 16px', cursor: 'pointer', borderBottom: '1px solid #ffffff08', position: 'relative' },
  convItemActive: { padding: '14px 16px', cursor: 'pointer', borderBottom: '1px solid #ffffff08', backgroundColor: '#1A2744', position: 'relative' },
  convName: { color: '#ffffff', fontSize: '14px', fontWeight: '600', margin: '0 0 4px 0' },
  convPreview: { color: '#ffffff60', fontSize: '12px', margin: '0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  unreadBadge: { position: 'absolute', top: '14px', right: '16px', backgroundColor: '#C49A28', color: '#1A2744', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700' },
  messageThread: { backgroundColor: '#0d1628', borderRadius: '0 10px 10px 0', border: '1px solid #ffffff15', borderLeft: 'none', display: 'flex', flexDirection: 'column' },
  noConvSelected: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  noConvText: { color: '#ffffff30', fontSize: '14px' },
  threadHeader: { padding: '16px 20px', borderBottom: '1px solid #ffffff10' },
  threadName: { color: '#ffffff', fontSize: '15px', fontWeight: '600', margin: '0' },
  messagesList: { flex: 1, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', minHeight: '300px', maxHeight: '400px' },
  messageSent: { alignSelf: 'flex-end', backgroundColor: '#C49A28', borderRadius: '12px 12px 0 12px', padding: '10px 14px', maxWidth: '70%' },
  messageReceived: { alignSelf: 'flex-start', backgroundColor: '#1A2744', border: '1px solid #ffffff15', borderRadius: '12px 12px 12px 0', padding: '10px 14px', maxWidth: '70%' },
  messageBody: { color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0', lineHeight: '1.4' },
  messageTime: { color: '#ffffff60', fontSize: '11px', margin: '0' },
  messageInput: { padding: '16px 20px', borderTop: '1px solid #ffffff10', display: 'flex', gap: '10px' },
  messageField: { flex: 1, backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px 14px', color: '#ffffff', fontSize: '14px', outline: 'none' },
  sendBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '10px 20px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
};

export default FacultyHall;